import { useState, useRef } from "react";
import { PaymentElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Shield, Lock } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation, Trans } from "react-i18next";
import { confirmWithWatchdog, STRIPE_CONFIRM_TIMEOUT } from "./InlinePayment";

export default function StripePaymentForm({
  onSuccess,
  onError,
  amount,
  deposit,
  currencySymbol = "$",
  ctaLabel,
  mode,
}: {
  onSuccess: () => void;
  onError: (msg: string) => void;
  amount: number;
  deposit: number;
  currencySymbol?: string;
  ctaLabel?: string;
  // Pay-later (task #394). When the booking client secret comes from a
  // SetupIntent (renter chose Pay later), Stripe.js requires confirmSetup
  // instead of confirmPayment. We branch on this flag rather than guessing
  // from the secret prefix to keep the API explicit.
  mode?: "payment" | "setup";
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const confirming = useRef(false);
  const isSetup = mode === "setup";

  const confirm = async () => {
    if (!stripe || !elements) return { error: { message: t("checkout.stripe_not_ready") } as { message?: string } };
    // Stripe forwards the visitor to `return_url` when a 3DS challenge is
    // required. Build it from the current URL so the locale segment (e.g.
    // /uk, /de/en) survives the round-trip — sending them to the bare host
    // would land them on the worldwide page (task #865).
    const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    if (isSetup) {
      return await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
    }
    return await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });
  };

  // Task #5991 — bound the confirm call so this standalone page (RetryPaymentPage
  // / ClaimPayPage) can't spin forever when an inline 3DS / wallet / "elements
  // store" rejection hangs. Mirrors the main checkout watchdog in InlinePayment.
  // On timeout we route to the page's honest recovery — onSuccess(), which
  // navigates the renter to the booking detail page where the REAL server-side
  // payment status is shown. We never fall through to onError here: a stalled
  // confirm may have left a live pre-auth, and reporting "payment failed" would
  // be dishonest and could prompt a double-submit.
  const runConfirm = async () => {
    const res = await confirmWithWatchdog(confirm);
    if (res === STRIPE_CONFIRM_TIMEOUT) {
      onSuccess();
      return;
    }
    const result = res;

    if (result.error) {
      onError(result.error.message || t("checkout.payment_failed"));
      setProcessing(false);
      confirming.current = false;
      return;
    }

    // C2 — guard the success path: only call onSuccess when the PaymentIntent
    // actually reached a chargeable status. confirmPayment resolving without
    // an error does NOT guarantee the intent succeeded — e.g. requires_action
    // (SCA that couldn't be handled inline) resolves cleanly but leaves the
    // card uncharged. Mirror the status gate on the saved-card path.
    if (!isSetup && "paymentIntent" in result && result.paymentIntent) {
      const { status } = result.paymentIntent;
      if (status !== "succeeded" && status !== "requires_capture" && status !== "processing") {
        onError(t("checkout.payment_not_complete", { status }));
        setProcessing(false);
        confirming.current = false;
        return;
      }
    }

    onSuccess();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || confirming.current) return;
    confirming.current = true;
    setProcessing(true);
    await runConfirm();
  };

  const handleExpressConfirm = async () => {
    if (!stripe || !elements || confirming.current) return;
    confirming.current = true;
    setProcessing(true);
    await runConfirm();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Apple Pay / Google Pay / Link — auto-detected per device. */}
      <ExpressCheckoutElement
        onConfirm={handleExpressConfirm}
        options={{ paymentMethods: { applePay: "auto", googlePay: "auto", link: "auto", paypal: "never" } }}
      />
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <span>{t("checkout.or_pay_with_card")}</span>
        <span className="h-px flex-1 bg-border" />
      </div>
      {/* Hide Stripe Link's inline signup wallet (email / mobile / name panel
          under the card fields). Renters who want Link can still use the
          Express Checkout button rendered above this form. */}
      <PaymentElement options={{ wallets: { link: "never" } }} />
      {deposit > 0 && (
        <Alert variant="warning" icon={Shield}>
          <AlertDescription className="text-xs">
            <Trans
              i18nKey="checkout.deposit_timing.generic_explainer"
              values={{ amount: `${currencySymbol}${deposit.toFixed(2)}` }}
              components={{ strong: <strong /> }}
            />
          </AlertDescription>
        </Alert>
      )}
      <button
        type="submit"
        disabled={!stripe || processing}
        className="primary-action cursor-pointer w-full rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <Spinner size="sm" />
            {t("common.processing")}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Lock className="h-3.5 w-3.5" />
            {ctaLabel || t("checkout.pay_amount", { symbol: currencySymbol, amount: amount.toFixed(2) })}
          </span>
        )}
      </button>
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <span>{t("checkout.powered_by")}</span>
        <svg className="h-[14px]" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe">
          <path d="M60 12.5C60 5.596 53.731 0 46.154 0H13.846C6.269 0 0 5.596 0 12.5S6.269 25 13.846 25h32.308C53.731 25 60 19.404 60 12.5Z" fill="#635BFF"/>
          <path d="M27.44 10.27c0-.79.65-1.1 1.72-1.1.55 0 1.24.11 1.93.39V7.42c-.65-.26-1.29-.36-1.93-.36-2.07 0-3.45 1.08-3.45 2.89 0 2.82 3.88 2.37 3.88 3.58 0 .93-.82 1.23-1.96 1.23-.85 0-1.83-.35-2.36-.65v2.17c.8.35 1.61.49 2.36.49 2.12 0 3.57-1.05 3.57-2.88-.01-3.04-3.9-2.5-3.9-3.62h.14Zm8.22-3.18-1.7.36v1.99h-1.14v2.01h1.14v3.12c0 1.59.77 2.42 2.36 2.42.65 0 1.14-.14 1.43-.3v-1.96c-.23.09-.98.09-1.31.09-.45 0-.78-.18-.78-.87v-2.5h2.09V9.44h-2.09V7.09Zm5.72 1.51a1.09 1.09 0 0 0-1.09 1.11c0 .62.48 1.11 1.09 1.11.62 0 1.1-.49 1.1-1.11 0-.61-.48-1.11-1.1-1.11ZM40.5 17h2.01V9.44H40.5V17Zm5.66-10.26h-2.01V17h2.01v-3.37c0-1.2.52-1.75 1.41-1.75.27 0 .59.05.85.13l.01-1.96c-.23-.07-.51-.12-.81-.12-.98 0-1.6.56-1.84 1.12l-.11-.61h-1.51v7.56Zm8.53 2.69c0-.82-.39-1.47-1.3-1.47-.6 0-1.12.36-1.39.89l-.03-.77h-1.68V17h2.01v-4.2c.35-.46.84-.6 1.18-.6.49 0 .67.28.67.82V17h2.01v-4.31c0-1.53-.69-3.26-2.47-3.26l1 .7Z" fill="#fff"/>
        </svg>
        <span>{t("checkout.encryption_label")}</span>
      </div>
    </form>
  );
}
